import axios from "axios";
import logo from "./assets/logo.png";
import { async } from "q";
import { baseUrl } from "./redux/actions/baseUrl";
import axiosInstance from "./axiosInstance";
import { toast } from "./utils/toast";
import TokenService from "./services/tokenService";

// const pushNotification = (body) => {
//     if (Notification.permission !== "granted") {
//         Notification.requestPermission();
//     } else {
//         const notification = Notification.showNotification("Misas", {
//             body: body,
//             icon: logo,
//         });
//         notification.onclick = () => {
//             window.location.href = "/notification";
//         };
//     }
// };

const pushNotification = (message) => {
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    navigator.serviceWorker.ready.then(registration => {
      registration.showNotification('Misas Health', {
        body: message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
        data: {
          dateOfArrival: Date.now(),
          primaryKey: 1
        }
      });
    });
  }
};

/**
 * Generate a cryptographically secure room name for Jitsi (alphanumeric only)
 * @param {string} appointmentId - Unique appointment identifier
 * @param {string} patientId - Patient identifier
 * @returns {string} Secure room name (Jitsi-safe)
 */
const generateSecureRoomName = (appointmentId, patientId) => {
    try {
        const array = new Uint8Array(16); // 128 bits of entropy
        crypto.getRandomValues(array);

        // Convert to base36 and remove non-alphanumerics (just in case)
        const secureRandom = Array.from(array, byte => byte.toString(36))
            .join('')
            .replace(/[^a-z0-9]/gi, '')
            .substring(0, 12);

        const timestamp = Date.now().toString().slice(-6);

        const hashedPatientId = btoa(patientId.toString())
            .replace(/[^a-zA-Z0-9]/g, '')
            .substring(0, 6);

        // Final room name: only alphanumeric, no hyphens or underscores
        const roomName = `misas${secureRandom}${timestamp}${hashedPatientId}`;

        return roomName;
    } catch (error) {
        const fallbackRandom = Math.random().toString(36).replace(/[^a-z0-9]/gi, '').substring(2, 14);
        return `misas${fallbackRandom}${Date.now().toString(36)}`;
    }
};

/**
 * Validate room name format and security (alphanumeric only)
 * @param {string} roomName
 * @returns {boolean}
 */
const validateRoomName = (roomName) => {
    if (!roomName || typeof roomName !== 'string') return false;
    if (roomName.length < 20) return false;

    // Only alphanumeric names allowed
    const roomNamePattern = /^misas[a-zA-Z0-9]+$/;
    return roomNamePattern.test(roomName);
};
  

// Create our number formatter.
const formatter = new Intl.NumberFormat('en-US', {
    // style: 'currency',
    currency: 'NGN',
});

/**
 * Securely clear all user data from localStorage and token storage
 */
function removeFromLocaStorage() {
    try {
        // Clear tokens using secure token service
        TokenService.clearTokens();
        
        // Clear other user data
        const keysToRemove = [
            "userInfo",
            "hospital_name",
            "hospital_phone",
            "type",
            "consultation_audio",
            "consultation_video",
            "hospital"
        ];

        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
        });
        
        console.log('localStorage and tokens cleared successfully');
    } catch (error) {
        console.error('Error clearing localStorage:', error);
    }
}

function debugLocalStorage() {
    const keys = [
        "userInfo",
        "hospital_name",
        "hospital_phone",
        "type",
        "consultation_audio",
        "consultation_video"
    ];

    console.log('=== localStorage Debug ===');
    keys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
            try {
                const parsed = JSON.parse(value);
                console.log(`${key}:`, parsed);
            } catch (e) {
                console.log(`${key}:`, value);
            }
        } else {
            console.log(`${key}: null/undefined`);
        }
    });
    
    // Debug token information
    console.log('=== Token Debug ===');
    const tokenInfo = TokenService.getTokenInfo();
    console.log('Token Info:', tokenInfo);
    console.log('==================');
}

/**
 * Enhanced token validation with proper security checks
 * @returns {boolean} - True if token is valid and user is authenticated
 */
async function check_token() {
    return true;
    try {
        // Check if token exists and is valid
        if (!TokenService.isTokenValid()) {
            console.warn('No valid token found');
            removeFromLocaStorage();
            return false;
        }

        // Check if user info exists
        if (!localStorage.getItem('userInfo')) {
            console.warn('No user info found');
            removeFromLocaStorage();
            return false;
        }

        // Check if token is about to expire
        if (TokenService.isTokenExpiringSoon()) {
            console.warn('Token is expiring soon');
            // For now, we'll let it continue but log the warning
            // Token refresh logic can be added here if needed
        }

        // For now, we'll trust the client-side token validation
        // Server-side validation can be added later if needed
        return true;
        
        // Commented out server-side validation to prevent 401 loops
        // const response = await axiosInstance.get('/check_token');
        // const { valid } = response.data;
        // if (!valid) {
        //     console.warn('Server rejected token');
        //     removeFromLocaStorage();
        //     return false;
        // }
        // return true;
    } catch (error) {
        console.error("An error occurred while checking the token:", error);
        removeFromLocaStorage();
        return false;
    }
}

const getHospitalId = async (data) => {
    const res = await axiosInstance.get(`/auth/get-hospital-id?qr_code=${encodeURIComponent(data)}`);
    console.log('getHospitalId', res.data);
    if (res?.data?.success) {
        localStorage.setItem('hospital', JSON.stringify(res?.data.data?.hospital.hospital_id));
        return true;
    } else {
        console.log('getHospitalId', res);
        toast.error('Something went wrong')
        return false;
    }
}

/**
 * Check if the selected appointment date/time is at least the given minutes ahead of now
 * @param {string} apptDate - YYYY-MM-DD
 * @param {string} apptTime - HH:MM
 * @param {number} minutesAhead - minimum minutes ahead required (default 60)
 * @returns {boolean}
 */
function isAppointmentAtLeastMinutesAhead(apptDate, apptTime, minutesAhead = 60) {
    if (!apptDate || !apptTime) return false;

    const [year, month, day] = apptDate.split('-').map(Number);
    const [hours, mins] = apptTime.split(':').map(Number);

    const appointment = new Date();
    appointment.setFullYear(year, (month - 1), day);
    appointment.setHours(hours, mins, 0, 0);

    const now = new Date();
    const threshold = new Date(now.getTime() + minutesAhead * 60 * 1000);

    return appointment.getTime() >= threshold.getTime();
}

export { pushNotification, check_token, formatter, getHospitalId, debugLocalStorage, generateSecureRoomName, validateRoomName, isAppointmentAtLeastMinutesAhead };